import { SUBJECT_VOCABULARY_BY_VERTICAL, type ResearchDepth } from '@ni/shared';
import type { AdminResearchSettings } from '../api/types';
import { budgetField, budgetFrom, budgetUnusable } from './budget';

/**
 * **The research panel's decisions, as functions** — the arrangement
 * `budget.ts` and `create-org.ts` establish, and what makes them testable in a
 * suite that carries no DOM.
 *
 * The bag is the operator's whole (2026-09-04, Soroush): the depth per status
 * and the spend limit per run name the cost and shape of the pipeline, which is
 * this surface's question rather than the office's.
 */

/**
 * The panel's form state. `''` is **unset** — the state a control shows as "Use
 * the default", annotated with what that default is — and the budget is a string
 * because it is a text field, where blank is "no limit".
 *
 * A form has no `undefined`: an unset select still has a value, and `''` is the
 * one the DOM gives it.
 */
export interface ResearchDraft {
  depthByStatus: Readonly<Record<string, ResearchDepth | ''>>;
  budget: string;
}

/**
 * The stored bag as the form shows it. Nothing is resolved: an organization that
 * chose full depth and one that chose nothing must not look the same, which is why the
 * wire carries an absent key for unset rather than the effective value.
 */
export function researchDraftOf(research: AdminResearchSettings): ResearchDraft {
  return {
    depthByStatus: research.depth_by_status ?? {},
    budget: budgetField(research.budget_usd ?? null),
  };
}

/**
 * Whether this build would put a control on the panel for that status — the test
 * {@link researchBagOf} drops an unrenderable depth by. A vertical this build
 * does not carry has no vocabulary to test against, so **every** status passes:
 * dropping them would be this app deciding about words it does not have.
 */
function isRenderableStatus(vertical: string, status: string): boolean {
  const vocabulary = SUBJECT_VOCABULARY_BY_VERTICAL[vertical];
  if (!vocabulary) return true;
  return (vocabulary.statuses as readonly string[]).includes(status);
}

/**
 * The draft as the wire carries it.
 *
 * **Every unset choice is an omitted key**, which the wire already reads as
 * "back to the default": naming `research` replaces the stored bag, so a key
 * left out of it is a choice taken back and `{}` takes them all back at once.
 * There is no key spelled out to clear one — including the budget, whose `null`
 * the server still accepts and which this panel has no reason to send, since an
 * emptied field is the same statement as an unset select.
 *
 * Exported because it is also how the panel decides whether anything moved: the
 * saved settings and the draft go through this one function, so the comparison
 * is between two bags built the same way rather than between a form and a
 * response.
 *
 * **A depth for a status the panel cannot render is dropped**, and that is the
 * one thing here that changes what is stored rather than only what is sent. A
 * `depth_by_status` key outside the org's current vocabulary — a status the
 * vertical had when it was written and does not have now — has no control on
 * screen and would be re-posted on every save, where the server refuses it by
 * name: an operator locked out of *every* research save by a control they cannot
 * see. Since the key names a relationship the organization no longer has, the
 * honest resolution is to let the next save clear it.
 */
export function researchBagOf(draft: ResearchDraft, vertical: string): AdminResearchSettings {
  const depth = Object.entries(draft.depthByStatus)
    .filter((entry): entry is [string, ResearchDepth] => entry[1] !== '')
    .filter(([status]) => isRenderableStatus(vertical, status));
  const budget = budgetFrom(draft.budget);
  return {
    ...(depth.length > 0 ? { depth_by_status: Object.fromEntries(depth) } : {}),
    ...(budget === null ? {} : { budget_usd: budget }),
  };
}

/**
 * Whether there is no save to offer — the button's `disabled` and the save
 * function's own first line, as one rule rather than two copies of it.
 *
 * Three reasons, and they are different reasons: nothing was changed, so there
 * is nothing to write; what was written in the budget field is not an amount, so
 * the server would refuse it and the field says so first; or the last save has
 * not answered yet.
 *
 * The unusable-budget case is checked on the draft rather than on what
 * {@link researchBagOf} produced, and has to be: a `-3` builds the same bag a
 * blank field does, so a comparison of bags would read it as "no limit, and no
 * change" and quietly offer nothing at all rather than saying what is wrong.
 */
export function researchSaveWithheld(field: {
  draft: ResearchDraft;
  saved: ResearchDraft;
  vertical: string;
  saving: boolean;
}): boolean {
  if (field.saving) return true;
  if (budgetUnusable(field.draft.budget)) return true;
  return (
    JSON.stringify(researchBagOf(field.draft, field.vertical)) ===
    JSON.stringify(researchBagOf(field.saved, field.vertical))
  );
}
